import { useState, useEffect, useMemo } from 'react'
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Line,
  ComposedChart,
} from 'recharts'
import { api } from '../../services/api'
import type {
  CombinedBillingResponse,
  CombinedBillingEntity,
} from '../../services/api'

// --- Helpers ---

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)

const formatCompact = (value: number) => {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}k`
  return `$${value}`
}

const GrowthBadge = ({ value, suffix = '%' }: { value: number; suffix?: string }) => {
  const isPositive = value > 0
  const isZero = value === 0
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${
      isZero ? 'text-gray-400' : isPositive ? 'text-green-400' : 'text-red-400'
    }`}>
      {!isZero && (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d={isPositive ? 'M5 10l7-7m0 0l7 7m-7-7v18' : 'M19 14l-7 7m0 0l-7-7m7 7V3'} />
        </svg>
      )}
      {isZero ? '--' : `${isPositive ? '+' : ''}${value.toFixed(1)}${suffix}`}
    </span>
  )
}

// Inline sparkline using SVG
const Sparkline = ({ data, color, width = 80, height = 24 }: {
  data: number[]
  color: string
  width?: number
  height?: number
}) => {
  if (data.length < 2) return <span className="text-gray-600 text-xs">--</span>
  const max = Math.max(...data, 1)
  const min = Math.min(...data, 0)
  const range = max - min || 1
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width
    const y = height - ((v - min) / range) * (height - 4) - 2
    return `${x},${y}`
  }).join(' ')

  return (
    <svg width={width} height={height} className="inline-block">
      <polyline fill="none" stroke={color} strokeWidth={1.5} points={points} />
      {/* Dot on last point */}
      {(() => {
        const last = data[data.length - 1]
        const x = width
        const y = height - ((last - min) / range) * (height - 4) - 2
        return <circle cx={x} cy={y} r={2} fill={color} />
      })()}
    </svg>
  )
}

// --- Custom Chart Tooltip ---

interface ChartTooltipProps {
  active?: boolean
  payload?: Array<{ value?: number; color?: string; dataKey?: string; name?: string }>
  label?: string
}

// --- Entity Detail Modal ---

function EntityDetailModal({ entity, onClose }: {
  entity: CombinedBillingEntity
  onClose: () => void
}) {
  const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']

  const chartData = entity.history.map(h => {
    const [, m] = h.period.split('-')
    const monthIdx = parseInt(m) - 1
    return {
      period: monthNames[monthIdx] || h.period,
      managed: h.managed,
      services: h.services,
      total: h.total,
      users: h.users,
    }
  })

  const totalAllTime = entity.history.reduce((s, h) => s + h.total, 0)

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-gray-800 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: entity.color }} />
            <div>
              <h3 className="text-lg font-bold text-white">{entity.name}</h3>
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  entity.type === 'managed' ? 'bg-blue-500/20 text-blue-300' : 'bg-purple-500/20 text-purple-300'
                }`}>
                  {entity.type === 'managed' ? 'Seguridad Gestionada' : 'Servicios Profesionales'}
                </span>
                {entity.planDisplayName && (
                  <span className="text-gray-500">{entity.planDisplayName}</span>
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Summary Cards */}
        <div className="p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-700/50 rounded-lg p-3">
            <p className="text-gray-400 text-xs">MRR Actual</p>
            <p className="text-xl font-bold text-white">{formatCurrency(entity.currentMrr)}</p>
          </div>
          <div className="bg-gray-700/50 rounded-lg p-3">
            <p className="text-gray-400 text-xs">Crecimiento MoM</p>
            <p className="text-xl font-bold"><GrowthBadge value={entity.mrrGrowth} /></p>
          </div>
          {entity.currentUsers !== undefined && (
            <div className="bg-gray-700/50 rounded-lg p-3">
              <p className="text-gray-400 text-xs">Usuarios</p>
              <p className="text-xl font-bold text-white">
                {entity.currentUsers}
                {entity.userDelta !== undefined && entity.userDelta !== 0 && (
                  <span className={`text-sm ml-1 ${entity.userDelta > 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {entity.userDelta > 0 ? '+' : ''}{entity.userDelta}
                  </span>
                )}
              </p>
            </div>
          )}
          <div className="bg-gray-700/50 rounded-lg p-3">
            <p className="text-gray-400 text-xs">Total Acumulado</p>
            <p className="text-xl font-bold text-green-400">{formatCurrency(totalAllTime)}</p>
          </div>
        </div>

        {/* Chart */}
        <div className="px-6 pb-4">
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#374151" />
                <XAxis dataKey="period" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#9ca3af' }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#9ca3af' }} tickFormatter={v => formatCompact(v)} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                  labelStyle={{ color: '#fff', fontWeight: 600 }}
                  formatter={(value: number, name: string) => [
                    formatCurrency(value),
                    name === 'managed' ? 'Paquete' : name === 'services' ? 'Servicios' : name
                  ]}
                />
                {entity.type === 'managed' && entity.history.some(h => h.users > 0) && (
                  <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false}
                    tick={{ fontSize: 11, fill: '#fbbf24' }} />
                )}
                {entity.type === 'managed' && (
                  <Bar dataKey="managed" fill={entity.color} radius={[4, 4, 0, 0]} name="managed" />
                )}
                {entity.history.some(h => h.services > 0) && (
                  <Bar dataKey="services" fill="#8B5CF6" radius={[4, 4, 0, 0]} name="services" />
                )}
                {entity.type === 'managed' && entity.history.some(h => h.users > 0) && (
                  <Line type="monotone" dataKey="users" stroke="#fbbf24" strokeWidth={2} dot={{ r: 3 }} yAxisId="right" name="Usuarios" />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Monthly Detail Table */}
        <div className="px-6 pb-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-400 border-b border-gray-700">
                <th className="py-2 text-left font-medium">Periodo</th>
                {entity.type === 'managed' && <th className="py-2 text-right font-medium">Usuarios</th>}
                <th className="py-2 text-right font-medium">Paquete</th>
                <th className="py-2 text-right font-medium">Servicios</th>
                <th className="py-2 text-right font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              {entity.history.map((h, i) => {
                const prev = i > 0 ? entity.history[i - 1] : null
                const totalGrowth = prev && prev.total > 0 ? ((h.total - prev.total) / prev.total) * 100 : 0
                const [y, m] = h.period.split('-')
                const label = `${monthNames[parseInt(m) - 1]} ${y}`
                return (
                  <tr key={h.period} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                    <td className="py-2 text-gray-300">{label}</td>
                    {entity.type === 'managed' && (
                      <td className="py-2 text-right text-gray-300">
                        {h.users}
                        {prev && h.users !== prev.users && (
                          <span className={`text-xs ml-1 ${h.users > prev.users ? 'text-green-400' : 'text-red-400'}`}>
                            {h.users > prev.users ? '+' : ''}{h.users - prev.users}
                          </span>
                        )}
                      </td>
                    )}
                    <td className="py-2 text-right text-blue-300">{h.managed > 0 ? formatCurrency(h.managed) : '--'}</td>
                    <td className="py-2 text-right text-purple-300">{h.services > 0 ? formatCurrency(h.services) : '--'}</td>
                    <td className="py-2 text-right text-white font-medium">
                      {formatCurrency(h.total)}
                      {i > 0 && totalGrowth !== 0 && (
                        <span className={`text-xs ml-1 ${totalGrowth > 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {totalGrowth > 0 ? '+' : ''}{totalGrowth.toFixed(0)}%
                        </span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// --- Main Component ---

interface RevenueAnalyticsProps {
  onCaptureSnapshot?: () => void
  snapshotLoading?: boolean
}

// Shape of the saas-metrics endpoint response (see worker.ts handleSaasMetrics).
interface SaasMetricsData {
  generatedAt: string
  current: {
    period: string | null
    mrr: number
    arr: number
    arpu: number | null
    activeTenants: number
    activeUsers: number
  }
  growth: {
    mom: number | null
    nrr: number | null
    monthlyChurnRate: number | null
    churnedTenants: number
    cohortSize: number
  }
  ltv: number | null
  series: Array<{ period: string; year: number; month: number; mrr: number; users: number; tenants: number }>
  forecast: Array<{ period: string; mrr: number }> | null
}

export default function RevenueAnalytics({ onCaptureSnapshot, snapshotLoading }: RevenueAnalyticsProps) {
  const [data, setData] = useState<CombinedBillingResponse | null>(null)
  const [saas, setSaas] = useState<SaasMetricsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedEntity, setSelectedEntity] = useState<CombinedBillingEntity | null>(null)
  const [viewMode, setViewMode] = useState<'all' | 'managed' | 'services'>('all')

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)
      // Fetch both datasets in parallel. SaaS metrics is additive — if
      // it fails we still render the legacy view.
      const [combined, saasResult] = await Promise.allSettled([
        api.getCombinedBilling({ startYear: 2025, startMonth: 1 }),
        api.getSaasMetrics(),
      ])
      if (combined.status !== 'fulfilled' || !combined.value.success || !combined.value.data) {
        throw new Error(combined.status === 'rejected'
          ? String(combined.reason)
          : combined.value.success === false ? combined.value.error || 'Error al cargar datos' : 'Error al cargar datos')
      }
      const responseData = (combined.value.data as unknown as CombinedBillingResponse)
      setData(responseData)
      if (saasResult.status === 'fulfilled' && saasResult.value.success && saasResult.value.data) {
        // apiRequest double-wraps — unwrap the inner `.data` if present.
        const raw = saasResult.value.data as unknown as { data?: SaasMetricsData } | SaasMetricsData
        const payload = (raw as { data?: SaasMetricsData }).data ?? (raw as SaasMetricsData)
        setSaas(payload)
      }
    } catch (err) {
      console.error('Error fetching combined billing:', err)
      setError(err instanceof Error ? err.message : 'Error desconocido')
    } finally {
      setLoading(false)
    }
  }

  // Build chart data
  const chartData = useMemo(() => {
    if (!data?.data) return []
    return data.data.map(p => ({
      period: p.periodLabel,
      managed: p.managedTotal,
      services: p.servicesTotal,
      total: p.total,
      users: p.totalUsers,
    }))
  }, [data])

  // Filtered entities
  const filteredEntities = useMemo(() => {
    if (!data?.entities) return []
    if (viewMode === 'managed') return data.entities.filter(e => e.type === 'managed')
    if (viewMode === 'services') return data.entities.filter(e => e.type === 'professional')
    return data.entities
  }, [data, viewMode])

  // Custom tooltip for main chart
  const MainChartTooltip = ({ active, payload, label }: ChartTooltipProps) => {
    if (!active || !payload || payload.length === 0) return null
    const managedVal = payload.find(p => p.dataKey === 'managed')?.value || 0
    const servicesVal = payload.find(p => p.dataKey === 'services')?.value || 0
    const usersVal = payload.find(p => p.dataKey === 'users')?.value
    return (
      <div className="bg-gray-800 border border-gray-600 rounded-lg shadow-lg p-3 min-w-[200px]">
        <p className="font-semibold text-white mb-2">{label}</p>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between gap-4">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-blue-400" />
              <span className="text-gray-300">Paquetes</span>
            </span>
            <span className="text-white font-medium">{formatCurrency(managedVal)}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm bg-purple-400" />
              <span className="text-gray-300">Servicios</span>
            </span>
            <span className="text-white font-medium">{formatCurrency(servicesVal)}</span>
          </div>
          <div className="border-t border-gray-600 pt-1 flex justify-between gap-4">
            <span className="text-gray-300 font-medium">Total</span>
            <span className="text-green-400 font-bold">{formatCurrency(managedVal + servicesVal)}</span>
          </div>
          {usersVal !== undefined && usersVal > 0 && (
            <div className="flex justify-between gap-4 text-yellow-400">
              <span>Usuarios</span>
              <span className="font-medium">{usersVal}</span>
            </div>
          )}
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-32 bg-gray-800 rounded-xl mb-4" />
          <div className="h-64 bg-gray-800 rounded-xl mb-4" />
          <div className="h-48 bg-gray-800 rounded-xl" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-500/20 border border-red-500 text-red-400 px-4 py-3 rounded-lg">
        {error}
        <button onClick={fetchData} className="ml-4 underline hover:no-underline">Reintentar</button>
      </div>
    )
  }

  if (!data) return null

  const { summary, totals } = data

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="bg-gray-800 rounded-xl p-4">
          <p className="text-gray-400 text-xs uppercase tracking-wide">MRR</p>
          <p className="text-2xl font-bold text-green-400 mt-1">{formatCurrency(summary.mrr)}</p>
          <GrowthBadge value={totals.growth} />
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <p className="text-gray-400 text-xs uppercase tracking-wide">ARR Proyectado</p>
          <p className="text-2xl font-bold text-white mt-1">{formatCurrency(summary.arr)}</p>
          <p className="text-gray-500 text-xs">MRR x 12</p>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <p className="text-gray-400 text-xs uppercase tracking-wide">Usuarios</p>
          <p className="text-2xl font-bold text-white mt-1">{summary.totalUsers}</p>
          {summary.userDelta !== 0 && (
            <span className={`text-xs ${summary.userDelta > 0 ? 'text-green-400' : 'text-red-400'}`}>
              {summary.userDelta > 0 ? '+' : ''}{summary.userDelta} vs mes anterior
            </span>
          )}
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <p className="text-gray-400 text-xs uppercase tracking-wide">ARPU</p>
          <p className="text-2xl font-bold text-white mt-1">{formatCurrency(summary.arpu)}</p>
          <p className="text-gray-500 text-xs">Revenue por usuario</p>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <p className="text-gray-400 text-xs uppercase tracking-wide">Paquetes</p>
          <p className="text-2xl font-bold text-blue-400 mt-1">{formatCurrency(summary.managedMrr)}</p>
          <p className="text-gray-500 text-xs">{data.tenants.length} clientes</p>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <p className="text-gray-400 text-xs uppercase tracking-wide">Servicios</p>
          <p className="text-2xl font-bold text-purple-400 mt-1">{formatCurrency(summary.servicesMrr)}</p>
          <p className="text-gray-500 text-xs">{data.clients.length} clientes</p>
        </div>
      </div>

      {/* Revenue Chart */}
      <div className="bg-gray-800 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-white">Revenue Mensual</h3>
            <p className="text-gray-400 text-sm">Paquetes + Servicios Profesionales</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Growth badge */}
            <GrowthBadge value={totals.growth} />
            {onCaptureSnapshot && (
              <button
                onClick={onCaptureSnapshot}
                disabled={snapshotLoading}
                className="flex items-center gap-2 px-3 py-1.5 bg-jarix-orange hover:bg-jarix-pink text-white rounded-lg text-sm transition-colors disabled:opacity-50"
                title="Capturar snapshot del mes actual"
              >
                {snapshotLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                )}
                Snapshot
              </button>
            )}
          </div>
        </div>

        {chartData.length === 0 ? (
          <div className="text-center py-12">
            <svg className="w-12 h-12 mx-auto mb-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <p className="text-gray-400">No hay datos historicos. Captura un snapshot para comenzar.</p>
          </div>
        ) : (
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 10, right: 40, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#374151" />
                <XAxis dataKey="period" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#9ca3af' }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#9ca3af' }}
                  tickFormatter={v => formatCompact(v)} />
                <YAxis yAxisId="users" orientation="right" axisLine={false} tickLine={false}
                  tick={{ fontSize: 11, fill: '#fbbf24' }} />
                <Tooltip content={<MainChartTooltip />} />
                <Legend
                  iconType="square" iconSize={10}
                  formatter={(value: string) => (
                    <span className="text-gray-400 text-xs">
                      {value === 'managed' ? 'Paquetes' : value === 'services' ? 'Servicios' : value === 'users' ? 'Usuarios' : value}
                    </span>
                  )}
                />
                <Bar dataKey="managed" stackId="revenue" fill="#3b82f6" radius={[0, 0, 0, 0]} name="managed" />
                <Bar dataKey="services" stackId="revenue" fill="#8b5cf6" radius={[4, 4, 0, 0]} name="services" />
                <Line type="monotone" dataKey="users" stroke="#fbbf24" strokeWidth={2}
                  dot={{ r: 3, fill: '#fbbf24' }} yAxisId="users" name="users" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Mini summary below chart */}
        <div className="flex gap-6 mt-4 pt-4 border-t border-gray-700 text-sm">
          <div>
            <span className="text-gray-400">Acumulado:</span>
            <span className="text-white font-semibold ml-2">{formatCurrency(totals.allTime)}</span>
          </div>
          <div>
            <span className="text-gray-400">Concentracion Top 1:</span>
            <span className={`font-semibold ml-2 ${summary.top1Share > 60 ? 'text-yellow-400' : 'text-gray-300'}`}>
              {summary.top1Share.toFixed(0)}%
            </span>
          </div>
          <div>
            <span className="text-gray-400">Top 3:</span>
            <span className="text-gray-300 font-semibold ml-2">{summary.top3Share.toFixed(0)}%</span>
          </div>
        </div>
      </div>

      {/* SaaS Metrics panel — NRR / LTV / churn + 3-month forecast.
          Additive to the legacy revenue chart above: those tiles are
          point-in-time $, these describe retention and trend. Rendered
          only when the saas-metrics endpoint returned data (graceful
          degradation if the API call failed). */}
      {saas && (
        <div className="bg-gray-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-white">SaaS Metrics</h3>
              <p className="text-gray-400 text-sm">
                Retención, LTV proxy y proyección · corte {saas.current.period || '—'}
              </p>
            </div>
            <span className="text-[11px] text-gray-500">
              {new Date(saas.generatedAt).toLocaleString('es-UY', { dateStyle: 'short', timeStyle: 'short' })}
            </span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            {/* NRR — > 100% = expansion, 100% = flat, < 100% = contraction. */}
            <SaasTile
              label="NRR (retención)"
              value={saas.growth.nrr !== null ? `${saas.growth.nrr.toFixed(1)}%` : 'N/D'}
              tone={saas.growth.nrr === null ? 'muted'
                : saas.growth.nrr >= 100 ? 'success'
                : saas.growth.nrr >= 90 ? 'warning'
                : 'critical'}
              sub={`Cohorte: ${saas.growth.cohortSize} tenants`}
            />
            <SaasTile
              label="Churn mensual"
              value={saas.growth.monthlyChurnRate !== null ? `${saas.growth.monthlyChurnRate.toFixed(2)}%` : 'N/D'}
              tone={saas.growth.monthlyChurnRate === null ? 'muted'
                : saas.growth.monthlyChurnRate === 0 ? 'success'
                : saas.growth.monthlyChurnRate <= 2 ? 'warning'
                : 'critical'}
              sub={`${saas.growth.churnedTenants} tenant(s) perdidos`}
            />
            <SaasTile
              label="LTV (proxy)"
              value={saas.ltv !== null ? formatCurrency(saas.ltv) : 'N/D'}
              tone={saas.ltv === null ? 'muted' : 'neutral'}
              sub="ARPU ÷ churn mensual"
            />
            <SaasTile
              label="ARPU"
              value={saas.current.arpu !== null ? formatCurrency(saas.current.arpu) : 'N/D'}
              tone="neutral"
              sub={`${saas.current.activeTenants} tenants activos`}
            />
          </div>

          {/* Forecast list — compact, intentionally not a chart to keep
              the extrapolation honest (linear, 3 points). */}
          {saas.forecast && saas.forecast.length > 0 ? (
            <div className="border-t border-gray-700 pt-4">
              <div className="flex items-baseline justify-between mb-2">
                <h4 className="text-white font-medium text-sm">Proyección a 3 meses</h4>
                <span className="text-[11px] text-gray-500">Extrapolación lineal · últimos 3 meses completos</span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {saas.forecast.map(f => (
                  <div key={f.period} className="bg-gray-900/50 border border-gray-700/50 rounded-lg p-3">
                    <div className="text-[11px] uppercase tracking-wider text-gray-500">{f.period}</div>
                    <div className="text-xl font-bold text-white mt-0.5">{formatCurrency(f.mrr)}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="border-t border-gray-700 pt-4 text-xs text-gray-500">
              Datos insuficientes para proyectar (se necesitan al menos 2 meses completos).
            </div>
          )}
        </div>
      )}

      {/* Entity Table */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-gray-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">Detalle por Cliente</h3>
          <div className="flex bg-gray-700 rounded-lg p-0.5">
            {(['all', 'managed', 'services'] as const).map(mode => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                  viewMode === mode ? 'bg-gray-600 text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                {mode === 'all' ? 'Todos' : mode === 'managed' ? 'Paquetes' : 'Servicios'}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900/50">
              <tr className="text-left text-gray-400 text-xs uppercase tracking-wide">
                <th className="px-4 py-3 font-medium">Cliente</th>
                <th className="px-4 py-3 font-medium">Tipo</th>
                <th className="px-4 py-3 font-medium text-right">MRR</th>
                <th className="px-4 py-3 font-medium text-right">Crecimiento</th>
                <th className="px-4 py-3 font-medium text-right">Usuarios</th>
                <th className="px-4 py-3 font-medium text-center">Tendencia</th>
                <th className="px-4 py-3 font-medium text-right">% Revenue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/50">
              {filteredEntities.map(entity => {
                const revenueShare = summary.mrr > 0 ? (entity.currentMrr / summary.mrr) * 100 : 0
                const sparkData = entity.history.map(h => h.total)

                return (
                  <tr
                    key={`${entity.type}-${entity.slug}`}
                    className="hover:bg-gray-700/30 cursor-pointer transition-colors"
                    onClick={() => setSelectedEntity(entity)}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: entity.color }} />
                        <div>
                          <span className="text-white font-medium">{entity.name}</span>
                          {entity.planDisplayName && (
                            <span className="text-gray-500 text-xs ml-2">{entity.planDisplayName}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        entity.type === 'managed' ? 'bg-blue-500/20 text-blue-300' : 'bg-purple-500/20 text-purple-300'
                      }`}>
                        {entity.type === 'managed' ? 'Paquete' : 'Servicios'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-white font-semibold">{formatCurrency(entity.currentMrr)}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <GrowthBadge value={entity.mrrGrowth} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      {entity.currentUsers !== undefined ? (
                        <span className="text-gray-300">
                          {entity.currentUsers}
                          {entity.userDelta !== undefined && entity.userDelta !== 0 && (
                            <span className={`text-xs ml-1 ${entity.userDelta > 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {entity.userDelta > 0 ? '+' : ''}{entity.userDelta}
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-gray-600">--</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Sparkline data={sparkData} color={entity.color} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-16 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{ width: `${Math.min(revenueShare, 100)}%`, backgroundColor: entity.color }}
                          />
                        </div>
                        <span className="text-gray-400 text-xs w-10 text-right">{revenueShare.toFixed(1)}%</span>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
            <tfoot className="bg-gray-900/50 border-t-2 border-gray-600">
              <tr>
                <td colSpan={2} className="px-4 py-3 text-white font-bold">
                  TOTAL {viewMode === 'managed' ? 'PAQUETES' : viewMode === 'services' ? 'SERVICIOS' : ''}
                </td>
                <td className="px-4 py-3 text-right text-green-400 font-bold text-lg">
                  {formatCurrency(filteredEntities.reduce((s, e) => s + e.currentMrr, 0))}
                </td>
                <td className="px-4 py-3 text-right">
                  <GrowthBadge value={totals.growth} />
                </td>
                <td className="px-4 py-3 text-right text-white font-bold">
                  {filteredEntities.reduce((s, e) => s + (e.currentUsers || 0), 0) || '--'}
                </td>
                <td colSpan={2} className="px-4 py-3" />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Plan Legend */}
      <div className="bg-gray-800/50 rounded-lg p-4">
        <h4 className="text-gray-400 text-sm font-medium mb-2">Planes de Precios</h4>
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 rounded bg-gray-500/20 text-gray-300 text-xs">Essentials</span>
            <span className="text-gray-400">$20/usuario</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 rounded bg-blue-500/20 text-blue-300 text-xs">Shield</span>
            <span className="text-gray-400">$40/usuario</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 rounded bg-purple-500/20 text-purple-300 text-xs">Advance</span>
            <span className="text-gray-400">$60/usuario</span>
          </div>
        </div>
      </div>

      {/* Entity Detail Modal */}
      {selectedEntity && (
        <EntityDetailModal entity={selectedEntity} onClose={() => setSelectedEntity(null)} />
      )}
    </div>
  )
}

// --- Subcomponent: SaasTile ---
// Small labeled value tile for the SaaS Metrics panel. `tone` drives
// the value color: green=healthy, yellow=watch, red=concerning, neutral
// for pure informational tiles (ARPU, LTV), muted for N/D states.
function SaasTile({ label, value, sub, tone }: {
  label: string
  value: string
  sub?: string
  tone?: 'success' | 'warning' | 'critical' | 'neutral' | 'muted'
}) {
  const valueColor = tone === 'success' ? 'text-green-400'
    : tone === 'warning' ? 'text-yellow-400'
    : tone === 'critical' ? 'text-red-400'
    : tone === 'muted' ? 'text-gray-500'
    : 'text-white'
  return (
    <div className="bg-gray-900/50 border border-gray-700/60 rounded-lg p-3">
      <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">{label}</div>
      <div className={`text-xl font-bold ${valueColor}`}>{value}</div>
      {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  )
}
